'use client';

import { useEffect, useRef, useState } from 'react';

/**
 * Recovery for a missing client chunk. A deploy that replaces the static export
 * can leave an open tab holding HTML that references chunk hashes no longer in
 * the bucket; the next lazy navigation then throws a chunk-load error. One
 * reload fetches the current build. The guard below makes that reload happen
 * at most once per window so a chunk that stays missing (bucket unreachable)
 * surfaces the fallback UI instead of a reload loop.
 */
export const CHUNK_RECOVERY_STORAGE_KEY = 'galapagos.web.chunk-recovery';

/** A recorded attempt younger than this blocks another automatic reload. */
export const CHUNK_RECOVERY_WINDOW_MS = 60_000;

const CHUNK_LOAD_MESSAGE_PATTERNS: readonly RegExp[] = [
  /Loading chunk [^\s]+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
];

/** True when `error` is the shape a browser raises for a chunk that no longer exists. */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  if (error.name === 'ChunkLoadError') return true;
  return CHUNK_LOAD_MESSAGE_PATTERNS.some((pattern) => pattern.test(error.message));
}

/** Storage surface the guard needs; `Storage` satisfies it. */
export interface RecoveryStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface ChunkRecoveryDeps {
  /** Performs the reload. Browser default: `window.location.reload()`. */
  reload: () => void;
  /** Where the attempt timestamp lives. Browser default: `window.sessionStorage`. */
  storage: () => RecoveryStorage;
  /** Clock. Default: `Date.now`. */
  now: () => number;
}

const browserDeps: ChunkRecoveryDeps = {
  reload: () => window.location.reload(),
  storage: () => window.sessionStorage,
  now: () => Date.now(),
};

/**
 * Reloads the page at most once per recovery window and reports whether it did.
 *
 * Returns `false` without reloading when a recorded attempt is younger than the
 * window, or when storage cannot be read or written: an in-memory guard would
 * reset on every reload and loop forever while the chunk stays missing, so a
 * member without storage sees the fallback and its manual Reload button.
 */
export function attemptChunkRecoveryReload(deps: Partial<ChunkRecoveryDeps> = {}): boolean {
  const { reload, storage, now } = { ...browserDeps, ...deps };
  let store: RecoveryStorage;
  let previous: string | null;
  try {
    store = storage();
    previous = store.getItem(CHUNK_RECOVERY_STORAGE_KEY);
  } catch {
    return false;
  }
  const current = now();
  const previousAt = previous === null ? Number.NaN : Number(previous);
  if (Number.isFinite(previousAt) && current - previousAt < CHUNK_RECOVERY_WINDOW_MS) {
    return false;
  }
  try {
    store.setItem(CHUNK_RECOVERY_STORAGE_KEY, String(current));
  } catch {
    return false;
  }
  reload();
  return true;
}

export type ChunkRecoveryState = 'reloading' | 'fallback';

/**
 * Decides what an error boundary renders for `error`. A chunk-load error starts
 * in `reloading` so the fallback never flashes before the reload lands; the
 * attempt itself runs once per mount from an effect (never a state initializer,
 * which React StrictMode double-invokes), and flips to `fallback` when the guard
 * refuses.
 */
export function useChunkRecovery(
  error: unknown,
  deps: Partial<ChunkRecoveryDeps> = {},
): ChunkRecoveryState {
  const chunkError = isChunkLoadError(error);
  const [state, setState] = useState<ChunkRecoveryState>(chunkError ? 'reloading' : 'fallback');
  const attempted = useRef(false);
  // The dependency bag is captured once so a new object identity on a
  // re-render never re-arms the effect; the attempt is intentionally once per mount.
  const depsRef = useRef(deps);
  useEffect(() => {
    if (!chunkError || attempted.current) return;
    attempted.current = true;
    if (!attemptChunkRecoveryReload(depsRef.current)) setState('fallback');
  }, [chunkError]);
  return state;
}
